// src/features/messages/ConversationList.tsx — left pane: header + controls + rows.
import { useState } from 'react';
import styled from 'styled-components';
import { IconButton } from '@/ui';
import { HydrationState } from '@/components/HydrationState';
import { useHydrationPending } from '@/hooks/useGlobalHydration';
import { ConversationFilters } from './ConversationFilters';
import { ConversationSearch } from './ConversationSearch';
import { ConversationRow } from './ConversationRow';
import { NewDmDialog } from './NewDmDialog';
import { CreateGroupDialog } from './CreateGroupDialog';
import { GroupInvitesBlock } from './GroupInvitesBlock';
import type { ConvoFilter } from './meta';
import type { Conversation } from '@/core/models/message';

const Pane = styled.aside`
  width: 320px; flex: none; display: flex; flex-direction: column; min-height: 0;
  border-right: 1px solid ${({ theme }) => theme.color.glassBorder};
`;
const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 14px 8px;
`;
const Heading = styled.h1`
  margin: 0; font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg}; font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;
const Scroll = styled.div`
  flex: 1; min-height: 0; overflow-y: auto; padding: 0 4px;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;
const Empty = styled.div`
  padding: 28px 16px; text-align: center;
  font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

export interface ConversationListProps {
  conversations: Conversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  filter: ConvoFilter;
  onFilterChange: (filter: ConvoFilter) => void;
  search: string;
  onSearchChange: (value: string) => void;
  /** Called once a new DM/group is created, or a group invite is accepted —
   *  the caller should select this conversation. */
  onConversationCreated: (id: string) => void;
  /** Threads awaiting acceptance — badges the Requests chip. Default 0. */
  requestsCount?: number;
}

export function ConversationList({
  conversations, activeId, onSelect, filter, onFilterChange, search, onSearchChange,
  onConversationCreated, requestsCount = 0,
}: ConversationListProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const pending = useHydrationPending('conversations');
  const isGroupView = filter === 'group';

  function handleCreated(id: string) {
    setDialogOpen(false);
    onConversationCreated(id);
  }

  return (
    <Pane>
      <Header>
        <Heading>Messages</Heading>
        <IconButton
          icon={isGroupView ? 'user-plus' : 'edit'}
          label={isGroupView ? 'New group' : 'New message'}
          onClick={() => setDialogOpen(true)}
        />
      </Header>
      <ConversationSearch value={search} onChange={onSearchChange} />
      <ConversationFilters
        active={filter}
        onChange={onFilterChange}
        requestsCount={requestsCount}
      />
      {isGroupView && <GroupInvitesBlock onAccepted={onConversationCreated} />}
      <Scroll>
        {pending ? (
          <HydrationState resource="conversations" label="your conversations" cards={2} />
        ) : conversations.length === 0 ? (
          <Empty>
            {filter === 'requests' ? 'No message requests.' : 'No conversations.'}
          </Empty>
        ) : (
          conversations.map((c) => (
            <ConversationRow key={c.id} convo={c} active={c.id === activeId} onSelect={onSelect} />
          ))
        )}
      </Scroll>
      {/*
       * Conditionally rendered (not just `open={dialogOpen}`) on purpose:
       * NewDmDialog/CreateGroupDialog own their own title/chips/error state,
       * and <Dialog> returning null when closed only unmounts ITS children —
       * a dialog component that stayed mounted with just an `open` prop would
       * keep that state alive across a close/reopen. Unmounting the dialog
       * component itself is what resets the form on next open.
       */}
      {dialogOpen &&
        (isGroupView ? (
          <CreateGroupDialog open onClose={() => setDialogOpen(false)} onCreated={handleCreated} />
        ) : (
          <NewDmDialog open onClose={() => setDialogOpen(false)} onCreated={handleCreated} />
        ))}
    </Pane>
  );
}
